import { useCallback, useEffect, useState, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import { CALL_STATE } from "src/constants/constants"
import JsSIP from "jssip"
import { setRinging, setConnected, setEnded } from "src/redux/crmCallingReducer"
import { message } from "antd"
import HRService from "src/services/HumanResource"
import { getSipConfigCache, getSipErrorMessage, setSipConfigCache } from "./SIPConfig"
import { pushErrorNotify } from "src/redux/notificationReducer"

function useCallJssip(phoneNumber, enabled) {
  const dispatch = useDispatch()
  const { callState } = useSelector(state => state.calling)

  const [timeElapsed, setTimeElapsed] = useState(0)
  const [enableMicro, setEnableMicro] = useState(true)
  const timerRef = useRef(null)
  const uaRef = useRef(null)
  const sessionRef = useRef(null)
  const audioRef = useRef(null)
  const audioUnlockedRef = useRef(false)
  const isInitializingRef = useRef(false)
  const isRegisteredRef = useRef(false)   // ← THÊM: track trạng thái đăng ký
  const pendingDialRef = useRef(false)

  // --- Khởi tạo audio remote (1 lần) ---
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = document.createElement("audio")
      audioRef.current.autoplay = true
      audioRef.current.style.display = "none"
      document.body.appendChild(audioRef.current)
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.remove()
        audioRef.current = null
      }
    }
  }, [])

  // --- Safe cleanup session ---
  const safeTerminate = useCallback(() => {
    try {
      const session = sessionRef.current
      if (session && (session.isInProgress() || session.isEstablished())) {
        session.terminate()
      }
    } catch (e) {
      console.warn("Terminate error:", e)
    }

    sessionRef.current = null
    setEnableMicro(true)

    if (audioRef.current?.srcObject) {
      try {
        audioRef.current.srcObject.getTracks().forEach(t => t.stop())
        audioRef.current.srcObject = null
      } catch (e) {
        console.warn("Audio cleanup error:", e)
      }
    }
  }, [])

  // --- Stop UA hoàn toàn ---
  const stopUA = useCallback(() => {
    pendingDialRef.current = false
    const ua = uaRef.current
    if (!ua) return
    try {
      safeTerminate()
      ua.stop()
    } catch (e) {
      console.warn("Stop UA error:", e)
    }
    uaRef.current = null
    isInitializingRef.current = false
    isRegisteredRef.current = false   // ← reset
  }, [safeTerminate])

  // --- Unlock audio ---
  const unlockAudio = useCallback(() => {
    if (!audioUnlockedRef.current && audioRef.current) {
      const playPromise = audioRef.current.play()
      if (playPromise !== undefined) {
        playPromise
          .then(() => { audioUnlockedRef.current = true })
          .catch(() => console.log("Audio chưa được phép autoplay"))
      }
    }
  }, [])

  // --- Attach remote stream ---
  // FIX: Dùng event `addtrack` để đảm bảo track sẵn sàng
  const attachStream = useCallback((session) => {
    const pc = session?.connection
    if (!pc || !audioRef.current) return

    const doAttach = () => {
      const remoteStream = new MediaStream()
      pc.getReceivers().forEach(r => {
        if (r.track) remoteStream.addTrack(r.track)
      })
      audioRef.current.srcObject = remoteStream
      audioRef.current.play().catch(e => console.log("Cannot play media:", e))
      console.log("Stream attached, tracks:", remoteStream.getTracks().length)
    }

    // Attach ngay nếu đã có track, đồng thời lắng nghe track mới
    doAttach()
    pc.addEventListener("track", doAttach)
  }, [])

  // --- Khởi tạo UA JsSIP ---
  useEffect(() => {
    if (!enabled) {
      stopUA()
      return
    }
    if (uaRef.current || isInitializingRef.current) return

    isInitializingRef.current = true

    async function initUA() {
      try {
        let sipConfig = getSipConfigCache()
        if (!sipConfig) {
          const res = await HRService.getContactCallTokenGmobile()
          const data = res?.success || {}
          sipConfig = {
            email: data.email,
            password: data.password,
            domain: data.domain || process.env.REACT_APP_SIP_DOMAIN,
            wsServer: data.link_socket || process.env.REACT_APP_SIP_WEBSOCKET,
            type: data.type,
          }
          setSipConfigCache(sipConfig) // 2. Lưu vào sessionStorage
        }

        const { email, password, domain, wsServer, type } = sipConfig

        console.log("SIP config:", { email, domain, wsServer, type })  // ← debug

        if (!email || !password || !domain || !wsServer || type !== "2") {
          message.error("Tài khoản chưa được gán đầu số")
          dispatch(setEnded({ data: { phoneNumber: "unknown", cause: "no-extension" } }))
          isInitializingRef.current = false
          return
        }

        if (uaRef.current) {
          isInitializingRef.current = false
          return
        }

        const socket = new JsSIP.WebSocketInterface(wsServer)
        const ua = new JsSIP.UA({
          sockets: [socket],
          uri: `sip:${email}@${domain};transport=wss`,
          password,
          display_name: email,
          extraHeaders: [
            `Contact: <sip:${email}@${domain}>`
          ],
          register: true,
          register_expires: 300,
          connection_recovery_min_interval: 2,
          connection_recovery_max_interval: 30,
          use_preloaded_route: false,
          session_timers: false,
          pcConfig: {
            iceServers: [],
          },
        })

        uaRef.current = ua
        isInitializingRef.current = false

        let retryCount = 0

        ua.on("connected", () => {
          retryCount = 0
          console.log("✅ UA WebSocket connected")
        })

        // FIX: Lắng nghe sự kiện registered/unregistered
        ua.on("registered", () => {
          isRegisteredRef.current = true
          console.log("✅ SIP Registered — sẵn sàng gọi")

          // Nếu user đã bấm gọi trong lúc chờ registered → tự dial luôn
          if (pendingDialRef.current) {
            pendingDialRef.current = false
            console.log("📲 Auto dial sau khi registered")
            dialCallInternal(ua)  // ← gọi hàm internal (xem bên dưới)
          }
        })

        ua.on("unregistered", () => {
          isRegisteredRef.current = false
          console.warn("⚠️ SIP Unregistered")
        })

        ua.on("registrationFailed", (e) => {
          isRegisteredRef.current = false
          console.error("❌ SIP Registration failed:", e?.cause, e?.response?.status_code)
          message.error(`Đăng ký SIP thất bại: ${e?.cause || "unknown"}`)
        })

        ua.on("disconnected", () => {
          isRegisteredRef.current = false
          const delay = Math.min(1000 * 2 ** retryCount, 30000)
          console.log(`UA disconnected → retry sau ${delay / 1000}s`)
          setTimeout(() => {
            try {
              if (uaRef.current === ua && !ua.isConnected()) {
                ua.start()
                retryCount++
              }
            } catch (err) {
              console.error("Reconnect UA error:", err)
            }
          }, delay)
        })

        ua.on("newRTCSession", e => {
          const session = e.session
          const phone = session.remote_identity?.user || "unknown"

          console.log("📞 newRTCSession:", session.direction, phone)

          // FIX: Chỉ terminate session CŨ, không động đến session mới
          const oldSession = sessionRef.current
          if (oldSession && oldSession !== session) {
            try {
              if (oldSession.isInProgress() || oldSession.isEstablished()) {
                oldSession.terminate()
              }
            } catch (err) {
              console.warn("Terminate old session error:", err)
            }
          }

          sessionRef.current = session

          if (session.direction === "incoming") {
            dispatch(setRinging({ data: { phoneNumber: phone } }))
            unlockAudio()
          }

          session.on("progress", () => {
            console.log("📞 progress")
            dispatch(setRinging({ data: { phoneNumber: phone } }))
            unlockAudio()
            setTimeout(() => attachStream(session), 300)
            // FIX: Không attachStream ở đây — track chưa sẵn sàng lúc progress
          })

          session.on("confirmed", () => {
            console.log("📞 confirmed — call connected")
            dispatch(setConnected({ data: { phoneNumber: phone } }))
            attachStream(session)   // ← Chỉ attach sau khi confirmed
          })

          session.on("ended", (ev) => {
            console.log("📞 ended, cause:", ev?.cause)
            dispatch(setEnded({ data: { phoneNumber: phone, cause: ev?.cause || "ended" } }))
            safeTerminate()
          })

          session.on("failed", ev => {
            console.error("📞 failed, cause:", ev?.cause, ev?.message)
            const errorMsg = getSipErrorMessage(ev?.cause, ev?.message)

            // Dùng pushErrorNotify có sẵn
            dispatch(pushErrorNotify({
              title: "Cuộc gọi thất bại",
              message: errorMsg,
            }))

            dispatch(setEnded({ data: { phoneNumber: phone, cause: ev?.cause || "failed" } }))
            safeTerminate()
          })
        })

        ua.start()
      } catch (err) {
        console.error("Init UA error:", err)
        isInitializingRef.current = false
        dispatch(setEnded({ data: { phoneNumber: "unknown", cause: "api-error" } }))
        message.error(err?.response?.data?.message || err.message || "Lỗi kết nối máy chủ")
      }
    }

    initUA()

    return () => { stopUA() }
  }, [enabled])

  // --- Hàm gọi internal (dùng được cả trong dialCall và auto-dial) ---
  const dialCallInternal = useCallback((ua) => {
    safeTerminate()
    unlockAudio()

    console.log("📲 Đang gọi:", phoneNumber)

    try {
      const session = ua.call(phoneNumber, {
        mediaConstraints: { audio: true, video: false },
        pcConfig: {
          iceServers: [],
          // // THÊM các dòng này
          // bundlePolicy: "max-bundle",
          // rtcpMuxPolicy: "require",
          // iceCandidatePoolSize: 10, // Pre-gather trước khi call
        },
        sessionTimersExpires: 120,
      })

      sessionRef.current = session

      session.on("progress", () => {
        console.log("📞 outgoing progress")
        dispatch(setRinging({ data: { phoneNumber } }))
        unlockAudio()
        setTimeout(() => attachStream(session), 300)
      })

      session.on("confirmed", () => {
        console.log("📞 outgoing confirmed")
        dispatch(setConnected({ data: { phoneNumber } }))
        // attachStream(session)
      })

      session.on("ended", (ev) => {
        console.log("📞 outgoing ended:", ev?.cause)
        dispatch(setEnded({ data: { phoneNumber, cause: ev?.cause || "ended" } }))
        safeTerminate()
      })

      session.on("failed", e => {
        console.error("📞 outgoing failed:", e?.cause, e?.message)
        dispatch(setEnded({ data: { phoneNumber, cause: e?.cause || "failed" } }))
        safeTerminate()
      })
    } catch (err) {
      console.error("dialCall error:", err)
      message.error("Không thể thực hiện cuộc gọi: " + (err.message || ""))
      dispatch(setEnded({ data: { phoneNumber, cause: "dial-error" } }))
    }
  }, [phoneNumber, dispatch, unlockAudio, safeTerminate, attachStream])
  // --- Gọi đi ---
  // --- dialCall: nếu chưa registered thì đánh dấu pending, chờ tự gọi ---
  const dialCall = useCallback(() => {
    const ua = uaRef.current

    if (!phoneNumber) {
      message.warning("Số điện thoại không hợp lệ")
      return
    }

    // Chưa có UA hoặc chưa registered → đánh dấu pending, KHÔNG báo lỗi
    if (!ua || !isRegisteredRef.current) {
      console.log("⏳ Chưa registered, đánh dấu pending dial...")
      pendingDialRef.current = true

      // Timeout 10s: nếu vẫn chưa registered thì mới báo lỗi
      setTimeout(() => {
        if (pendingDialRef.current) {
          pendingDialRef.current = false
          console.error("❌ Timeout chờ SIP registered")
          message.error("Không thể kết nối SIP server, vui lòng thử lại")
          dispatch(setEnded({ data: { phoneNumber, cause: "register-timeout" } }))
        }
      }, 10000)

      return
    }

    dialCallInternal(ua)
  }, [phoneNumber, dispatch, dialCallInternal])

  // --- Kết thúc ---
  const terminateCall = useCallback(() => {
    const phone = sessionRef.current?.remote_identity?.user || phoneNumber || "unknown"
    safeTerminate()
    dispatch(setEnded({ data: { phoneNumber: phone, cause: "terminated" } }))
  }, [dispatch, safeTerminate, phoneNumber])

  // --- Nhận cuộc gọi ---
  const answerCall = useCallback(() => {
    const session = sessionRef.current
    if (!session) return
    unlockAudio()
    session.answer({ mediaConstraints: { audio: true, video: false } })
    dispatch(setConnected({ data: { phoneNumber: session.remote_identity.user } }))
    attachStream(session)
  }, [dispatch, unlockAudio, attachStream])

  // --- Hold / Unhold ---
  const holdCall = useCallback(() => sessionRef.current?.hold(), [])
  const unHoldCall = useCallback(() => sessionRef.current?.unhold(), [])

  // --- Mute / Unmute ---
  const mute = useCallback(() => { sessionRef.current?.mute({ audio: true }); setEnableMicro(false) }, [])
  const unMute = useCallback(() => { sessionRef.current?.unmute({ audio: true }); setEnableMicro(true) }, [])
  const onHandleMicro = useCallback(() => {
    enableMicro ? mute() : unMute()
  }, [enableMicro, mute, unMute])

  // --- Đếm thời gian ---
  useEffect(() => {
    if (callState === CALL_STATE.CONNECTED) {
      setTimeElapsed(0)
      timerRef.current = setInterval(() => setTimeElapsed(prev => prev + 1), 1000)
    } else {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    return () => { clearInterval(timerRef.current); timerRef.current = null }
  }, [callState])

  return {
    dialCall, terminateCall, answerCall,
    holdCall, unHoldCall,
    timeElapsed, enableMicro, onHandleMicro,
    unlockAudio,
  }
}

export default useCallJssip